import {
  Box,
  Container,
  Flex,
  HStack,
  useBreakpointValue,
  VStack,
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useVisualViewport } from '~/common/hooks/useVisualViewport';
import { AgentNotesCard } from '../../components/BuyerOfferAnalysis/AgentNotesCard';
import { BuyerOfferAnalysisHeader } from '../../components/BuyerOfferAnalysis/BuyerOfferAnalysisHeader';
import { BuyerOfferAnalysisTopTags } from '../../components/BuyerOfferAnalysis/BuyerOfferAnalysisTopTags';
import { CompsCard } from '../../components/BuyerOfferAnalysis/Comps/CompsCard';
import { EditCompsModal } from '../../components/BuyerOfferAnalysis/Comps/EditCompsModal/EditCompsModal';
import { ViewCompsModal } from '../../components/BuyerOfferAnalysis/Comps/ViewCompsModal/ViewCompsModal';
import { useBuyerOfferAnalysisContext } from '../../components/BuyerOfferAnalysis/Context/BuyerOfferAnalysisContext';
import { ContingenciesTable } from '../../components/BuyerOfferAnalysis/KeyTermsAndContingencies/ContingenciesTable';
import { KeyTermsTable } from '../../components/BuyerOfferAnalysis/KeyTermsAndContingencies/KeyTermsTable';
import { BuyerOfferAnalysisLeftSidebar } from '../../components/BuyerOfferAnalysis/LeftSidebar/BuyerOfferAnalysisLeftSidebar';
import { OfferScoreCard } from '../../components/BuyerOfferAnalysis/OfferScore/OfferScoreCard';
import { PurchasePriceCard } from '../../components/BuyerOfferAnalysis/PurchasePrice/PurchasePriceCard';
import { ShareWithBuyerModal } from '../../components/BuyerOfferAnalysis/ShareWithBuyer/ShareWithBuyerModal';
import { CONTAINER_PADDINGS } from '../../components/Layouts/Container';
import {
  CHAT_WIDTH,
  MAIN_HEADER_HEIGHT,
} from '../BuyerOfferAnalysis/constants';
import { ContingenciesMobileCards } from '../BuyerOfferAnalysis/KeyTermsAndContingencies/Mobile/ContingenciesMobileCards';
import { KeyTermsMobileCards } from '../BuyerOfferAnalysis/KeyTermsAndContingencies/Mobile/KeyTermsMobileCards';
import { BuyerAndListingInfo } from '../BuyerOfferAnalysis/Mobile/BuyerAndListingInfo';
import { EditBuyerOfferAnalysisPartyModal } from '../BuyerOfferAnalysis/Modals/EditBuyerOfferAnalysisPartyModal';
import { MobileOfferScoreFooter } from './Mobile/MobileOfferScoreFooter';
import { OfferScoreCardChat } from './OfferScoreCardChat';

export function OfferScorecard() {
  const {
    isViewOnly,
    isChatOpen,
    setIsChatOpen,
    headerHeight,
    isMobile,
    buyerOfferAnalysis,
  } = useBuyerOfferAnalysisContext();
  const { height } = useVisualViewport();
  const showLeftSidebar = useBreakpointValue({ base: false, xl: true });
  const containerRef = useRef<HTMLDivElement>(null);
  const offerScoreCardRef = useRef<HTMLDivElement>(null);
  const isOfferScoreHidden = buyerOfferAnalysis?.hideOfferScore;
  const [isScrolledPastOfferScoreCard, setIsScrolledPastOfferScoreCard] =
    useState(false);

  useEffect(() => {
    const onViewportScroll = debounce(() => {
      window.document.scrollingElement?.scrollTo(0, 0);
      // console.log(window.visualViewport);
    }, 300);
    window.visualViewport?.addEventListener('scroll', onViewportScroll);

    return () =>
      window.visualViewport?.removeEventListener('scroll', onViewportScroll);
  }, [height]);

  // IntersectionObserver to detect when we scroll past the OfferScoreCard
  useEffect(() => {
    if (!offerScoreCardRef.current) return;

    const observerOptions = {
      root: null, // Use the viewport as root
      rootMargin: `-${MAIN_HEADER_HEIGHT} 0px 0px 0px`, // subtract header margin
      threshold: 0, // Trigger when 0% of the element is visible
    };

    const observerCallback: IntersectionObserverCallback = (entries) => {
      const [entry] = entries;
      // We've scrolled past the element when it's not intersecting
      setIsScrolledPastOfferScoreCard(!entry.isIntersecting);
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );
    observer.observe(offerScoreCardRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isMobile, isViewOnly]);

  return (
    <VStack
      ref={containerRef}
      alignItems="stretch"
      height={{ base: height, xl: 'initial' }}
      overflow={{ base: 'hidden', xl: 'initial' }}
      spacing={0}
    >
      <BuyerOfferAnalysisHeader
        showOfferScore={
          isScrolledPastOfferScoreCard && !isMobile && !isOfferScoreHidden
        }
        onOfferCoachClick={() => setIsChatOpen((prev) => !prev)}
      />
      {!showLeftSidebar && (
        <Box
          data-testid="buyer-and-listing-info-desktop"
          display={{ base: 'none', xl: 'block' }}
          pr={{ base: 0, xl: isChatOpen ? CHAT_WIDTH : 0 }}
          transition="all 0.3s"
          width="100%"
        >
          <BuyerAndListingInfo />
        </Box>
      )}

      <Flex
        bg="bg.main"
        direction={{ base: 'column', xl: 'row' }}
        flexGrow={1}
        height="100%"
        justifyContent="space-between"
        overflow={{ base: 'auto', xl: 'initial' }}
        pb={{ base: 4, xl: 0 }}
        position="relative"
        pt={{ base: 0, xl: headerHeight }}
      >
        {!showLeftSidebar && (
          <Box
            data-testid="buyer-and-listing-info-mobile"
            display={{ base: 'block', xl: 'none' }}
            pr={{ base: 0, xl: isChatOpen ? CHAT_WIDTH : 0 }}
            transition="all 0.3s"
            width="100%"
          >
            <BuyerAndListingInfo />
          </Box>
        )}
        {showLeftSidebar && <BuyerOfferAnalysisLeftSidebar />}
        <Container
          centerContent
          bg="bg.main"
          maxW={{ base: '100vw', md: '100%' }}
          minW={{ base: '100vw', md: 'container.sm' }}
          {...CONTAINER_PADDINGS}
          {...(isMobile && { px: 0, pb: 0 })}
        >
          <VStack gap={{ base: 3, xl: 4 }} width="100%">
            <BuyerOfferAnalysisTopTags />
            <AgentNotesCard />
            <Flex
              flexDirection={{ base: 'column', xl: 'row' }}
              gap={4}
              px={{ base: 4, xl: 0 }}
              width="100%"
            >
              {isMobile && (
                <HStack alignItems="stretch" justifyContent="stretch">
                  <PurchasePriceCard />
                  {(!isOfferScoreHidden || !isViewOnly) && (
                    <OfferScoreCard size="md" />
                  )}
                </HStack>
              )}
              <CompsCard />
              {!isMobile && (
                <>
                  <PurchasePriceCard />
                  {(!isOfferScoreHidden || !isViewOnly) && (
                    <Box ref={offerScoreCardRef}>
                      <OfferScoreCard size="lg" />
                    </Box>
                  )}
                </>
              )}
            </Flex>
            {!isMobile ? (
              <>
                <KeyTermsTable />
                <ContingenciesTable />
              </>
            ) : (
              <>
                <KeyTermsMobileCards />
                <ContingenciesMobileCards />
              </>
            )}
          </VStack>
          {!isViewOnly && (
            <>
              <EditCompsModal />
              <ShareWithBuyerModal />
              <EditBuyerOfferAnalysisPartyModal />
            </>
          )}
          {!!isViewOnly && <ViewCompsModal />}
        </Container>

        {!isViewOnly && <OfferScoreCardChat />}
      </Flex>
      {isMobile && <MobileOfferScoreFooter />}
    </VStack>
  );
}
